import React, { useEffect, useId, useRef, useState } from 'react';
import {
  loadGooglePlacesScript,
  parseGooglePlaceDetails,
  type ParsedAddress,
} from '../../utils/googlePlacesAddress';
import Label from './Label';

interface AddressAutocompleteInputProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onPlaceSelect?: (parsed: ParsedAddress) => void;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  error?: string;
}

export default function AddressAutocompleteInput({
  label,
  value,
  onChange,
  onPlaceSelect,
  required = false,
  disabled = false,
  placeholder = 'Start typing an address...',
  error = '',
}: AddressAutocompleteInputProps) {
  const fieldId = useId();
  const inputName = `aa-place-${fieldId.replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const containerRef = useRef<HTMLDivElement>(null);
  const autocompleteRef =
    useRef<google.maps.places.PlaceAutocompleteElement | null>(null);
  const onChangeRef = useRef(onChange);
  const onPlaceSelectRef = useRef(onPlaceSelect);
  const requiredRef = useRef(required);
  const lastSyncedValueRef = useRef(value);
  const [isReady, setIsReady] = useState(false);
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onPlaceSelectRef.current = onPlaceSelect;
  }, [onPlaceSelect]);

  useEffect(() => {
    requiredRef.current = required;
  }, [required]);

  useEffect(() => {
    if (!apiKey || disabled) return;

    let cancelled = false;

    loadGooglePlacesScript(apiKey)
      .then(() => {
        if (!cancelled) {
          setIsReady(true);
        }
      })
      .catch(() => {
        // Autocomplete unavailable; user can still type the address manually.
      });

    return () => {
      cancelled = true;
    };
  }, [apiKey, disabled]);

  useEffect(() => {
    if (!isReady || !containerRef.current || disabled) {
      return;
    }

    let cancelled = false;
    let autocomplete: google.maps.places.PlaceAutocompleteElement | null = null;

    const handleSelect = async (
      event: google.maps.places.PlacePredictionSelectEvent,
    ) => {
      const placePrediction = event.placePrediction;
      if (!placePrediction) return;

      const place = placePrediction.toPlace();
      await place.fetchFields({
        fields: [
          'displayName',
          'formattedAddress',
          'addressComponents',
          'location',
        ],
      });

      const parsed = parseGooglePlaceDetails(place);
      if (!parsed.formattedAddress || !autocomplete) return;

      autocomplete.value = parsed.formattedAddress;
      lastSyncedValueRef.current = parsed.formattedAddress;
      onChangeRef.current(parsed.formattedAddress);
      onPlaceSelectRef.current?.(parsed);
    };

    const handleInput = () => {
      if (!autocomplete) return;
      const nextValue = autocomplete.value;
      lastSyncedValueRef.current = nextValue;
      onChangeRef.current(nextValue);
    };

    const mountAutocomplete = async () => {
      const { PlaceAutocompleteElement } =
        await google.maps.importLibrary('places');
      if (cancelled || !containerRef.current) return;

      autocomplete = new PlaceAutocompleteElement({
        includedPrimaryTypes: ['street_address', 'premise', 'subpremise'],
      });
      autocomplete.id = fieldId;
      autocomplete.name = inputName;
      autocomplete.placeholder = placeholder;
      autocomplete.disabled = disabled;
      autocomplete.required = requiredRef.current;
      autocomplete.value = value;
      lastSyncedValueRef.current = value;

      autocomplete.addEventListener('gmp-select', (event) => {
        void handleSelect(event);
      });
      autocomplete.addEventListener('input', handleInput);

      containerRef.current.replaceChildren(autocomplete);
      autocompleteRef.current = autocomplete;
    };

    void mountAutocomplete();

    return () => {
      cancelled = true;
      if (autocomplete) {
        autocomplete.removeEventListener('input', handleInput);
      }
      containerRef.current?.replaceChildren();
      autocompleteRef.current = null;
    };
  }, [disabled, fieldId, inputName, isReady, placeholder]);

  useEffect(() => {
    if (autocompleteRef.current && value !== lastSyncedValueRef.current) {
      autocompleteRef.current.value = value;
      lastSyncedValueRef.current = value;
    }
  }, [value]);

  useEffect(() => {
    if (autocompleteRef.current) {
      autocompleteRef.current.disabled = disabled;
    }
  }, [disabled]);

  useEffect(() => {
    if (autocompleteRef.current) {
      autocompleteRef.current.required = required;
    }
  }, [required]);

  return (
    <div>
      <Label htmlFor={fieldId}>
        {label} {required && <span className="text-error-500">*</span>}
      </Label>
      <div
        ref={containerRef}
        className="address-autocomplete-host"
        aria-required={required}
      />
      {!apiKey && (
        <p className="mt-1.5 text-xs text-amber-600">
          Address suggestions need REACT_APP_GOOGLE_MAPS_API_KEY. Add it to .env
          and restart the dev server.
        </p>
      )}
      {error && <p className="mt-1.5 text-xs text-error-600">{error}</p>}
    </div>
  );
}
